import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRoutes } from 'constants/apiKeys';
import baseFetcher from 'shared/helpers/baseFetcher';

const NOTIFICATION_SETTINGS_KEY = 'notification-settings';

export type NotificationChannel = 'email' | 'push' | 'both';

export interface NotificationTypeSettingResponse {
  enabled: boolean;
  channel: NotificationChannel;
}

export interface NotificationSettingsResponse {
  reminder_subscribed: boolean;
  shift_reminder_subscribed: boolean;
  new_event_notifications: NotificationTypeSettingResponse;
  shift_report_notifications: NotificationTypeSettingResponse;
  shift_reminder_notifications: NotificationTypeSettingResponse;
  autofill_notification_schedule: boolean;
  shift_notification_time: string | null;
  shift_notification_weekdays: number[] | null;
}

type NotificationTypeField =
  | 'new_event_notifications'
  | 'shift_report_notifications'
  | 'shift_reminder_notifications';

export type NotificationSettingsUpdate = Partial<
  Omit<NotificationSettingsResponse, NotificationTypeField>
> &
  Partial<
    Record<NotificationTypeField, Partial<NotificationTypeSettingResponse>>
  >;

export function applyNotificationSettingsUpdate(
  current: NotificationSettingsResponse,
  update: NotificationSettingsUpdate
): NotificationSettingsResponse {
  const mergeTypeSetting = (field: NotificationTypeField) => ({
    ...current[field],
    ...update[field],
  });

  return {
    ...current,
    ...update,
    new_event_notifications: mergeTypeSetting('new_event_notifications'),
    shift_report_notifications: mergeTypeSetting('shift_report_notifications'),
    shift_reminder_notifications: mergeTypeSetting(
      'shift_reminder_notifications'
    ),
  };
}

export function restoreNotificationSettingsFields(
  current: NotificationSettingsResponse,
  previous: NotificationSettingsResponse,
  update: NotificationSettingsUpdate
): NotificationSettingsResponse {
  const fields = Object.keys(update) as (keyof NotificationSettingsResponse)[];
  return fields.reduce(
    (restored, field) => ({ ...restored, [field]: previous[field] }),
    current
  );
}

export function useNotificationSettings() {
  return useQuery<NotificationSettingsResponse>({
    queryKey: [NOTIFICATION_SETTINGS_KEY],
    queryFn: () => baseFetcher(apiRoutes.notificationSettings()),
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  const queryKey = [NOTIFICATION_SETTINGS_KEY];

  return useMutation<
    void,
    unknown,
    NotificationSettingsUpdate,
    { previous?: NotificationSettingsResponse }
  >({
    mutationKey: queryKey,
    mutationFn: (data) =>
      baseFetcher(apiRoutes.notificationSettings(), {
        method: 'PATCH',
        data,
      }),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<NotificationSettingsResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData(
          queryKey,
          applyNotificationSettingsUpdate(previous, update)
        );
      }
      return { previous };
    },
    onError: (_error, update, context) => {
      const previous = context?.previous;
      if (!previous) return;
      queryClient.setQueryData<NotificationSettingsResponse>(
        queryKey,
        (current) =>
          current &&
          restoreNotificationSettingsFields(current, previous, update)
      );
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: queryKey }) === 1) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
